import React, { useEffect, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from 'react-apollo';
import pathOr from 'ramda/es/pathOr';
import { useFrameHooks } from '@xingternal/hub';
import { captureException, withConfig } from '@xingternal/hops';
import { appStatsQuery, badgesSeenMutation, predictiveSearchQuery, searchTrackingMutation, } from './graphql';
const defaultAppStatsPollInterval = 60000; // defaults to 1 min
const withHubData = (WrappedComponent) => {
    const HubData = ({ 
    // Refresh every minute - or override NOTIFICATIONS_POLL_INTERVAL env var
    refetchPeriodically = false, config, ...props }) => {
        var _a;
        const { useInitialData } = useFrameHooks();
        const { user, features, featureSwitches, hashedUserId, loading: loadingMainQuery, } = (useInitialData && useInitialData()) || {};
        const [notifications, setNotifications] = useState({});
        const appStatsPollInterval = parseInt(`${(_a = config === null || config === void 0 ? void 0 : config.appStatsPollInterval) !== null && _a !== void 0 ? _a : defaultAppStatsPollInterval}`, 10);
        const apolloClient = useApolloClient();
        const [predictiveSearch, setPredictiveSearch] = useState({});
        const [mutateBadgeSeen] = useMutation(badgesSeenMutation);
        const [trackSearch] = useMutation(searchTrackingMutation);
        const { loading: loadingAppStatsQuery, data: appStatsData = {}, error: appStatsQueryError, refetch, } = useQuery(appStatsQuery, {
            pollInterval: refetchPeriodically ? appStatsPollInterval : 0,
        });
        if (appStatsQueryError) {
            captureException(appStatsQueryError, {
                info: 'The AppStatsQuery has returned an error!',
            });
        }
        const { viewer: appStatsViewer = {} } = appStatsData;
        const { appStats } = appStatsViewer;
        useEffect(() => {
            if (appStats) {
                setNotifications(appStats);
            }
        }, [appStats]);
        const acknowledgeNotification = (badgeNames) => {
            if (Array.isArray(badgeNames)) {
                mutateBadgeSeen({ variables: { badgeNames } });
            }
            else {
                mutateBadgeSeen({ variables: { badgeNames: [badgeNames] } });
            }
        };
        const changeNotification = (key, count) => {
            const data = apolloClient.readQuery({
                query: appStatsQuery,
            });
            // Only update if values are different
            if (data.viewer.appStats[key] === count)
                return;
            // A new copy is needed because readQuery results has a `writable: false` descriptor.
            const newData = {
                ...data,
                viewer: {
                    ...data.viewer,
                    appStats: { ...data.viewer.appStats, [key]: count },
                },
            };
            // Update Apollo Cache
            apolloClient.writeQuery({ query: appStatsQuery, data: newData });
            // Update Xing State
            setNotifications({ ...data.viewer.appStats });
        };
        const refreshNotifications = () => {
            const refetcher = refetch || apolloClient.refetch;
            refetcher({ variables: { notificationsOnly: true } });
        };
        const executePredictiveSearch = async ({ searchTerm = '', consumer }) => {
            const searchResults = await apolloClient.query({
                query: predictiveSearchQuery,
                variables: { query: searchTerm, consumer },
            });
            const predictiveSearch = pathOr({}, ['data', 'viewer', 'predictiveSearch'], searchResults);
            setPredictiveSearch(predictiveSearch);
        };
        const executeSearchTracking = (data) => {
            trackSearch({
                variables: { input: { source: 'MESSAGES', messages: data } },
            });
        };
        const resetPredictiveSearch = () => { };
        return (React.createElement(WrappedComponent, { ...{
                user,
                features,
                featureSwitches,
                hashedUserId,
                loadingMainQuery,
                acknowledgeNotification,
                changeNotification,
                executePredictiveSearch,
                executeSearchTracking,
                loadingAppStatsQuery,
                notifications,
                refreshNotifications,
                resetPredictiveSearch,
                predictiveSearch,
                type: 'apollo',
            }, ...props }));
    };
    HubData.displayName = `withConfig(withHubData(${WrappedComponent.displayName || WrappedComponent.name || 'Component'}))`;
    return withConfig(HubData);
};
export { withHubData };
//# sourceMappingURL=withHubData.js.map